import { FileItem, OptionWithRank, SignatureData, ViewMode } from '@playbooks/action-cards-utils/src'
import { QuestionTypes, Question as QuestionData } from '@playbooks/core/src/interfaces/ActionCard'
import { cn } from '@playbooks/ui/lib/utils'

import TextInput from './components/TextInput'
import EmailInput from './components/EmailInput'
import NumberInput from './components/NumberInput'
import { Checkbox } from './components/Checkbox'
import DatePicker from './components/DatePicker'
import Dropdown from './components/Dropdown'
import LongTextInput from './components/LongTextInput'
import FileUpload from './components/FileUpload'
import PercentageInput from './components/PercentageInput'
import AmountInput from './components/AmountInput'
import FieldWrapper from './components/FieldWrapper'
import PhoneInput from './components/PhoneInput'
import Signature from './components/Signature'
import { useAppContext } from '../../contexts/AppContext'
import Formula from './components/Formula'

interface QuestionProps {
  data: QuestionData
}

export default function Question({ data }: QuestionProps) {
  const {
    update,
    resetHighlights,
    settings: { viewMode },
  } = useAppContext()

  const handleChange = (value: unknown) => {
    resetHighlights([data.id])

    let args = [data, 'value', value] as const

    switch (data.type) {
      case QuestionTypes.CHECKBOX:
        args = [data, 'value', Boolean(value)]
        break
      case QuestionTypes.DROPDOWN: {
        const valueAsOption = value as OptionWithRank
        args = [data, 'value', valueAsOption.id]
        break
      }
      case QuestionTypes.FILE: {
        const files = value as FileItem[]
        args = [data, 'value', files]
        break
      }
    }

    update(...args)
  }

  return (
    <FieldWrapper
      isDocView={viewMode === ViewMode.DOC}
      question={data}
      onChange={handleChange}
      render={(onChange, invalid) => {
        switch (data.type) {
          case QuestionTypes.TEXT:
            return (
              <QuestionWrapper>
                <TextInput question={data} onChange={onChange} invalid={invalid} />
              </QuestionWrapper>
            )
          case QuestionTypes.PERCENTAGE:
            return (
              <QuestionWrapper>
                <PercentageInput max={100} min={-100} question={data} onChange={onChange} invalid={invalid} />
              </QuestionWrapper>
            )
          case QuestionTypes.AMOUNT:
            return (
              <QuestionWrapper>
                <AmountInput question={data} onChange={onChange} invalid={invalid} />
              </QuestionWrapper>
            )
          case QuestionTypes.LONG_TEXT:
            return (
              <QuestionWrapper>
                <LongTextInput question={data} onChange={onChange} invalid={invalid} />
              </QuestionWrapper>
            )
          case QuestionTypes.EMAIL:
            return (
              <QuestionWrapper>
                <EmailInput question={data as QuestionData<string>} onChange={onChange} invalid={invalid} />
              </QuestionWrapper>
            )
          case QuestionTypes.NUMBER:
            return (
              <QuestionWrapper>
                <NumberInput question={data} onChange={onChange} invalid={invalid} />
              </QuestionWrapper>
            )

          case QuestionTypes.CHECKBOX:
            return <Checkbox question={data} onChange={onChange} invalid={invalid} />

          case QuestionTypes.DROPDOWN:
            return (
              <QuestionWrapper>
                <Dropdown question={data} onChange={onChange} invalid={invalid} />
              </QuestionWrapper>
            )

          case QuestionTypes.FILE:
            return <FileUpload question={data as QuestionData<FileItem[]>} onChange={onChange} invalid={invalid} />

          case QuestionTypes.DATE:
            return (
              <QuestionWrapper>
                <DatePicker type="date" question={data} onChange={onChange} invalid={invalid} />
              </QuestionWrapper>
            )
          case QuestionTypes.DATE_TIME:
            return (
              <QuestionWrapper>
                <DatePicker type="datetime-local" question={data} onChange={onChange} invalid={invalid} />
              </QuestionWrapper>
            )
          case QuestionTypes.PHONE:
            return (
              <QuestionWrapper>
                <PhoneInput question={data as QuestionData<string>} onChange={onChange} invalid={invalid} />
              </QuestionWrapper>
            )
          case QuestionTypes.SIGNATURE:
            return (
              <QuestionWrapper>
                <Signature onChange={onChange} value={data.value as SignatureData} />
              </QuestionWrapper>
            )
          case QuestionTypes.FORMULA:
            return (
              <QuestionWrapper>
                <Formula question={data as QuestionData<number>} onChange={onChange} invalid={invalid} />
              </QuestionWrapper>
            )
          default:
            return <div>Unsupported question type</div>
        }
      }}
    />
  )
}

function QuestionWrapper({ children }: { children: React.ReactNode }) {
  const {
    settings: { viewMode },
  } = useAppContext()

  const isDocView = viewMode === ViewMode.DOC
  return <div className={cn(isDocView && 'bg-zinc-200 dark:bg-zinc-700')}>{children}</div>
}
